// What the New contact form sends, and what it refuses to send.
//
// This is a separate module from NewCustomerInline.jsx for one reason: there is
// no DOM test harness in this repository, so anything that lives inside a React
// component is untestable here. The component is left holding state and markup;
// every decision the form makes is here, as a pure function, and
// test/web/new-contact-form.test.js runs it under `node --test`.
//
// It deliberately MIRRORS the server rather than replacing it. The authority is
// src/routes/api/directory.js §4.5 — the same required fields, the same maximum
// lengths (which are the column widths from db/migrations), and the same refusal
// of control and text-direction characters (src/lib/validate.js `UNSAFE_TEXT`).
// Duplicating those three facts buys the agent an instant answer instead of a
// round trip mid-call; it does not make the client a gate. A request that got
// past this and should not have is still a 422.

/**
 * Name, phone and position. NOT email.
 *
 * The desk is phone-first (SPEC.md §2.1): the people who ring it are cashiers,
 * drivers and assistant managers, and many of them have no work address. Email
 * was the only required field until migration 018, which is why agents were
 * inventing addresses to get past this form.
 */
export const REQUIRED_FIELDS = ['name', 'phone', 'position'];

/** Column widths, from db/migrations 001 and 018. `company` is nvarchar(max). */
export const MAX_LENGTHS = { name: 200, phone: 50, position: 100, email: 320 };

/**
 * The same set src/lib/validate.js refuses for `noControlChars` fields: C0
 * controls, DEL + C1, the line/paragraph separators, and the bidi
 * embeddings/overrides/isolates that reorder rendered text without changing the
 * stored bytes. Printable characters are all allowed — `O'Brien` and
 * `Cashier (nights)` are real answers.
 */
export const UNSAFE_TEXT = /[\u0000-\u001F\u007F-\u009F\u2028\u2029\u202A-\u202E\u2066-\u2069]/;

const LABELS = { name: 'Name', phone: 'Phone', position: 'Position', email: 'Email' };

/** The form's initial state. `company` is pre-filled from the chosen organization. */
export function initialValues(organization) {
  return { name: '', phone: '', position: '', email: '', company: organization?.name ?? '' };
}

/**
 * Turns form state into the body for `POST /api/customers`, or into the reason
 * it cannot be sent.
 *
 * Returns `{ problems, body, focus }`:
 *   - `problems` is `[{ field, message }]` and is empty when the body is usable.
 *   - `body` is null whenever `problems` is non-empty.
 *   - `focus` names the field the caret should go to, which is the first field
 *     with a problem — an agent with someone on the line should not have to read
 *     an error to find out which box is wrong.
 *
 * Optional fields are OMITTED when blank rather than sent as `""` or `null`.
 * That matters for `email`: `""` fails the server's `minLength: 1` and would be
 * a 422 on exactly the phone-only contact this form now exists to create.
 *
 * `location` is only ever sent alongside its own `organization` — a mismatched
 * pair is 422 VALIDATION_FAILED (docs/API.md §4.5).
 */
export function buildNewContactBody(values, { organization = null, location = null } = {}) {
  const trimmed = {};
  for (const key of ['name', 'phone', 'position', 'email', 'company']) {
    trimmed[key] = (values?.[key] ?? '').trim();
  }

  const problems = [];
  for (const field of REQUIRED_FIELDS) {
    if (!trimmed[field]) problems.push({ field, message: `${LABELS[field]} is required.` });
  }
  for (const [field, max] of Object.entries(MAX_LENGTHS)) {
    if (trimmed[field].length > max) {
      problems.push({ field, message: `${LABELS[field]} must be at most ${max} characters.` });
    }
  }
  for (const field of ['name', 'phone', 'position']) {
    if (UNSAFE_TEXT.test(trimmed[field])) {
      problems.push({ field, message: `${LABELS[field]} contains a character that is not allowed.` });
    }
  }

  if (problems.length) return { problems, body: null, focus: problems[0].field };

  const body = { name: trimmed.name, phone: trimmed.phone, position: trimmed.position };
  if (trimmed.email) body.email = trimmed.email;
  if (trimmed.company) body.company = trimmed.company;
  if (organization) body.organization_id = organization.id;
  if (organization && location) body.location_id = location.id;

  return { problems, body, focus: null };
}

/** One line for the alert region: the first problem, or the only one. */
export function problemMessage(problems) {
  if (!problems?.length) return null;
  if (problems.length === 1) return problems[0].message;
  return problems.map((p) => p.message).join(' ');
}
